import { z } from 'zod';
import { NotificationChannel, NotificationEvent, NotificationStatus } from '../enums.js';
import { ALL_PERMISSIONS } from '../permissions.js';
import { TEMPLATE_VARIABLES, findUnknownVariables } from '../reminders.js';
import { emailSchema, nameSchema, optionalNameSchema, passwordSchema, phoneSchema, searchSchema, uuidSchema, } from './common.js';
export const createAdminUserSchema = z.object({
    firstName: nameSchema,
    middleName: optionalNameSchema,
    lastName: nameSchema,
    email: emailSchema,
    phone: phoneSchema,
    roleId: uuidSchema,
    /** Omit to have the system email an invitation instead of setting a password. */
    password: passwordSchema.optional(),
    sendInvitation: z.boolean().default(true),
});
export const updateAdminUserSchema = z.object({
    firstName: nameSchema.optional(),
    middleName: optionalNameSchema,
    lastName: nameSchema.optional(),
    phone: phoneSchema.optional(),
    roleId: uuidSchema.optional(),
    status: z.enum(['ACTIVE', 'INACTIVE', 'SUSPENDED']).optional(),
});
const permissionSchema = z.enum(ALL_PERMISSIONS);
export const roleSchema = z.object({
    name: z
        .string()
        .trim()
        .min(3)
        .max(60)
        .regex(/^[A-Za-z0-9 _-]+$/, 'Use letters, numbers, spaces, hyphens and underscores.'),
    description: z.string().trim().max(500).optional(),
    permissions: z.array(permissionSchema).min(1, 'Grant at least one permission.'),
});
export const notificationTemplateSchema = z
    .object({
    name: z.string().trim().min(3).max(100),
    channel: z.nativeEnum(NotificationChannel),
    event: z.nativeEnum(NotificationEvent),
    /** Email only. SMS and WhatsApp have no subject line. */
    subject: z.string().trim().max(200).optional(),
    body: z.string().trim().min(1, 'Write the message body.').max(5000),
    isActive: z.boolean().default(true),
})
    .refine((data) => data.channel !== NotificationChannel.EMAIL || Boolean(data.subject?.trim()), { message: 'Email templates need a subject line.', path: ['subject'] })
    .superRefine((data, ctx) => {
    const unknown = findUnknownVariables(data.body);
    if (unknown.length > 0) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['body'],
            message: `Unknown placeholder(s): ${unknown.map((v) => `{{${v}}}`).join(', ')}. Available: ${TEMPLATE_VARIABLES.map((v) => `{{${v}}}`).join(', ')}`,
        });
    }
});
export const reminderStageSchema = z.object({
    key: z
        .string()
        .trim()
        .min(2)
        .max(50)
        .regex(/^[a-z0-9_]+$/, 'Use lowercase letters, numbers and underscores.'),
    label: z.string().trim().min(2).max(100),
    offsetDays: z.coerce.number().int().min(-365).max(365),
    channels: z.array(z.nativeEnum(NotificationChannel)).min(1, 'Pick at least one channel.'),
    event: z.nativeEnum(NotificationEvent),
    notifyGuarantors: z.boolean().default(false),
    enabled: z.boolean().default(true),
});
export const broadcastSchema = z
    .object({
    channels: z.array(z.nativeEnum(NotificationChannel)).min(1),
    subject: z.string().trim().max(200).optional(),
    body: z.string().trim().min(1).max(5000),
    /** Who receives it. `MEMBER_IDS` pairs with an explicit list. */
    audience: z.enum([
        'ALL_MEMBERS',
        'VERIFIED_MEMBERS',
        'MEMBERS_WITH_ACTIVE_LOANS',
        'MEMBERS_IN_ARREARS',
        'MEMBER_IDS',
    ]),
    memberIds: z.array(uuidSchema).max(5000).optional(),
    scheduledFor: z.coerce.date().optional(),
})
    .refine((data) => data.audience !== 'MEMBER_IDS' || (data.memberIds?.length ?? 0) > 0, { message: 'Select at least one member.', path: ['memberIds'] });
export const notificationSearchSchema = searchSchema.extend({
    channel: z.nativeEnum(NotificationChannel).optional(),
    status: z.nativeEnum(NotificationStatus).optional(),
    event: z.nativeEnum(NotificationEvent).optional(),
    memberId: uuidSchema.optional(),
    sentFrom: z.coerce.date().optional(),
    sentTo: z.coerce.date().optional(),
});
export const auditLogSearchSchema = searchSchema.extend({
    userId: uuidSchema.optional(),
    action: z.string().max(50).optional(),
    entity: z.string().max(60).optional(),
    entityId: z.string().max(64).optional(),
    from: z.coerce.date().optional(),
    to: z.coerce.date().optional(),
});
export const settingsUpdateSchema = z.object({
    key: z
        .string()
        .trim()
        .min(2)
        .max(80)
        .regex(/^[a-z0-9_.]+$/, 'Use lowercase letters, numbers, dots and underscores.'),
    value: z.union([z.string(), z.number(), z.boolean(), z.null(), z.record(z.unknown())]),
});
export const reportRequestSchema = z.object({
    report: z.enum([
        'MEMBER_LIST',
        'NEW_MEMBERS',
        'KYC_PENDING',
        'LOANS_BY_PERIOD',
        'LOANS_BY_PRODUCT',
        'ACTIVE_LOANS',
        'COMPLETED_LOANS',
        'DEFAULTED_LOANS',
        'DISBURSEMENTS',
        'COLLECTIONS',
        'OUTSTANDING_BALANCES',
        'OVERDUE_BALANCES',
        'REPAYMENT_PERFORMANCE',
        'GUARANTOR_EXPOSURE',
        'COMMUNICATIONS',
    ]),
    from: z.coerce.date().optional(),
    to: z.coerce.date().optional(),
    loanProductId: uuidSchema.optional(),
    format: z.enum(['CSV', 'XLSX', 'PDF', 'JSON']).default('CSV'),
});
//# sourceMappingURL=admin.js.map